import React, { useState, useEffect, useRef } from 'react';
import MainLayout from '../user-dashboard/MainLayout';
import ReactPlayer from 'react-player';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { 
  fetchSessionStatusFast, 
  startSession, 
  updateSessionProgress, 
  completeSession,
  supabase
} from '../../supabase';

import QuizSession00 from './QuizSession00';

const CheckMarkSVG = ({ size = 16, color = "#1B365D" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20,6 9,17 4,12"></polyline>
  </svg>
);

const PlayButtonSVG = ({ size = 32, color = "#1B365D" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="m380-300 280-180-280-180v360ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"/>
  </svg>
);

const PauseButtonSVG = ({ size = 32, color = "#1B365D" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="M360-320h80v-320h-80v320Zm160 0h80v-320h-80v320ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z"/>
  </svg>
);

const RewindButtonSVG = ({ size = 24, color = "#374151" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="M440-240 200-480l240-240 56 56-183 184 183 184-56 56Zm264 0L464-480l240-240 56 56-183 184 183 184-56 56Z"/>
  </svg>
);

const ForwardButtonSVG = ({ size = 24, color = "#374151" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="M383-480 200-664l56-56 240 240-240 240-56-56 183-184Zm264 0L464-664l56-56 240 240-240 240-56-56 183-184Z"/>
  </svg>
);

const FullscreenSVG = ({ size = 16, color = "#374151" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="M200-200v-160h80v80h80v80H200Zm400 0v-80h80v-80h80v160H600ZM200-600v-160h160v80h-80v80h-80Zm480 0v-80h-80v-80h160v160h-80Z"/>
  </svg>
);

const LockSVG = ({ size = 16, color = "#374151" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" height={size} viewBox="0 -960 960 960" width={size} fill={color}>
    <path d="M240-80q-33 0-56.5-23.5T160-160v-400q0-33 23.5-56.5T240-640h40v-80q0-83 58.5-141.5T480-920q83 0 141.5 58.5T680-720v80h40q33 0 56.5 23.5T800-560v400q0 33-23.5 56.5T720-80H240Zm0-80h480v-400H240v400Zm240-120q33 0 56.5-23.5T560-360q0-33-23.5-56.5T480-440q-33 0-56.5 23.5T400-360q0 33 23.5 56.5T480-280ZM360-640h240v-80q0-50-35-85t-85-35q-50 0-85 35t-35 85v80ZM240-160v-400 400Z"/>
  </svg>
);

const SessionPage00 = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [sessionCompleted, setSessionCompleted] = useState(false);
  const [isVideoCompleted, setIsVideoCompleted] = useState(false);
  const [userId, setUserId] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [loading, setLoading] = useState(true);
  const currentSessionNumber = 0;
  
  // Quiz states
  const [showQuiz, setShowQuiz] = useState(null);
  const [quizCompleted, setQuizCompleted] = useState(false);
  
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentVideo, setCurrentVideo] = useState('/assets/FFW_Intro_compressed.mp4');
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  
  const [shouldRestorePosition, setShouldRestorePosition] = useState(null);
  const [isRestoring, setIsRestoring] = useState(false);
  const [hasStartedWatching, setHasStartedWatching] = useState(false);
  const [completedFirstWatch, setCompletedFirstWatch] = useState(false);
  const [volume, setVolume] = useState(0.8);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [showCaptions, setShowCaptions] = useState(true);
  const [currentCaption, setCurrentCaption] = useState('');
  const [captions, setCaptions] = useState([]);

  const playerRef = useRef(null);
  const overlayRef = useRef(null);
  const [overlayTimeout, setOverlayTimeout] = useState(null);

  const sessionOverview = [
    { title: 'Taking Charge of Orofacial Pain', desc: 'Understanding chronic pain and mind-body connection' },
    { title: 'Managing Emotional Challenges in Orofacial Pain', desc: 'Managing emotional challenges with mindfulness' },
    { title: 'Working with Negative Thoughts in Orofacial Pain', desc: 'Working with negative thoughts and beliefs' },
    { title: 'Not Letting Orofacial Pain Get the Best of Us', desc: 'Re-engaging in meaningful life activities' },
    { title: 'Facing Forward: Rolling with Orofacial Pain & Staying Resilient', desc: 'Building long-term resilience strategies' }
  ];

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (user?.id) {
      setUserId(user.id);
    }
  }, [user]);

  // Load and parse VTT captions
  useEffect(() => {
    const loadCaptions = async () => {
      try {
        const response = await fetch('/assets/vtt/FFWSession0Captions.vtt');
        const vttText = await response.text();
        
        // Parse VTT file
        const lines = vttText.split('\n');
        const parsedCaptions = [];
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          
          // Look for timestamp lines (format: 00:00:00.000 --> 00:00:05.000)
          if (line.includes('-->')) {
            const [startStr, endStr] = line.split('-->').map(s => s.trim());
            
            // Parse time to seconds
            const parseTime = (timeStr) => {
              const parts = timeStr.split(':');
              const hours = parseInt(parts[0]) || 0;
              const minutes = parseInt(parts[1]) || 0;
              const seconds = parseFloat(parts[2]) || 0;
              return hours * 3600 + minutes * 60 + seconds;
            };
            
            const start = parseTime(startStr);
            const end = parseTime(endStr);
            
            // Get caption text (next non-empty line)
            let captionText = '';
            for (let j = i + 1; j < lines.length; j++) {
              const textLine = lines[j].trim();
              if (textLine === '' || textLine.includes('-->')) break;
              if (!textLine.match(/^\d+$/)) { // Skip cue numbers
                captionText += (captionText ? ' ' : '') + textLine;
              }
            }
            
            if (captionText) {
              parsedCaptions.push({ start, end, text: captionText });
            }
          }
        }
        
        setCaptions(parsedCaptions);
        console.log('✅ Loaded', parsedCaptions.length, 'captions');
      } catch (error) {
        console.error('Error loading captions:', error);
      }
    };
    
    loadCaptions();
  }, []);

  // Update current caption based on video position
  useEffect(() => {
    if (!showCaptions || captions.length === 0) {
      setCurrentCaption('');
      return;
    }
    
    const currentCue = captions.find(
      caption => playedSeconds >= caption.start && playedSeconds < caption.end
    );
    
    setCurrentCaption(currentCue ? currentCue.text : '');
}, [playedSeconds, captions, showCaptions]);

  // Cleanup overlay timeout on unmount
  useEffect(() => {
    return () => {
      if (overlayTimeout) {
        clearTimeout(overlayTimeout);
      }
    };
  }, [overlayTimeout]);

  const handleCaptionToggle = () => {
    setShowCaptions(!showCaptions);
  };

  const handleFullscreen = () => {
    const videoContainer = playerRef.current?.wrapper?.parentElement;
    if (!videoContainer) return;

    if (!document.fullscreenElement) {
      videoContainer.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch(err => {
        console.log('Error attempting to enable fullscreen:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch(err => {
        console.log('Error attempting to exit fullscreen:', err);
      });
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Close volume slider when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showVolumeSlider && !event.target.closest('.volume-control')) {
        setShowVolumeSlider(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showVolumeSlider]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const skipBackward = () => {
    if (playerRef.current && videoDuration > 0) {
      const newTime = Math.max(0, playedSeconds - 10);
      playerRef.current.seekTo(newTime, 'seconds');
      setPlayedSeconds(newTime);
      if (!isPlaying) setIsPlaying(true);
    }
  };

  const skipForward = () => {
    if (playerRef.current && videoDuration > 0 && isVideoCompleted) {
      const newTime = Math.min(videoDuration - 5, playedSeconds + 10);
      playerRef.current.seekTo(newTime, 'seconds');
      setPlayedSeconds(newTime);
      if (!isPlaying) setIsPlaying(true);
    }
  };

  const handleProgressBarClick = (e) => {
    if (videoDuration === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const newTime = (clickX / rect.width) * videoDuration;
    if (newTime <= playedSeconds || isVideoCompleted) {
      if (playerRef.current) {
        playerRef.current.seekTo(newTime, 'seconds');
        setPlayedSeconds(newTime);
        if (!isPlaying) setIsPlaying(true);
      }
    }
  };

  const handleProgress = (progress) => {
    if (isRestoring) return;
    const currentPosition = progress.playedSeconds;
    if (!hasStartedWatching && currentPosition > 5) {
      setHasStartedWatching(true);
    }
    setPlayedSeconds(currentPosition);
    const isNearEnd = videoDuration > 0 && (videoDuration - currentPosition) < 30;
    if (!isNearEnd && Math.floor(currentPosition) % 3 === 0 && currentPosition > 0) {
      saveVideoProgress(currentPosition);
    }
  };

  const saveVideoProgress = async (currentSeconds) => {
    try {
      if (!userId || videoDuration === 0 || currentSeconds < 5) return;
      const progressPercentage = Math.round((currentSeconds / videoDuration) * 100);
      if (progressPercentage >= 95) return;
      await updateSessionProgress(userId, currentSessionNumber, {
        video_progress_percentage: progressPercentage,
        last_video_position: currentSeconds,
        updated_at: new Date().toISOString()
      });
    } catch (error) {
      console.error("Error saving video progress:", error);
    }
  };

  const handleVideoEnd = async () => {
    setIsPlaying(false);
    setIsVideoCompleted(true);
    setCompletedFirstWatch(true);
    setShouldRestorePosition(null);
    try {
      if (!userId) return;
      await updateSessionProgress(userId, currentSessionNumber, {
        video_watched: true,
        video_completed: true,
        video_progress_percentage: 100,
        video_watch_time_seconds: Math.floor(videoDuration),
        last_video_position: 0,
        video_watched_timestamp: Date.now(),
        video_completion_date: new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      console.error("Error updating video completion:", error);
    }
  };

  const handleQuizCompletion = async (quizData) => {
    try {
      if (!userId) return;

      console.log("🎯 Quiz completed for Introduction");

      await updateSessionProgress(userId, currentSessionNumber, {
        quiz_completed: true,
        quiz_score: quizData?.score || 5,
        quiz_answers: quizData?.answers || [],
        quiz_attempts: quizData?.attempts || 1,
        quiz_time_taken_seconds: quizData?.timeTaken || 0,
        session_completed: true,
        session_fully_completed: true
      });

      setQuizCompleted(true);
      setSessionCompleted(true);
      setShowQuiz(null);
      
      console.log("✅ Introduction quiz completed and session marked complete");
    } catch (error) {
      console.error("❌ Error completing quiz:", error);
    }
  };

  const handleSessionStart = async (userId, sessionNumber) => {
    try {
      const success = await startSession(userId, sessionNumber);
      if (success) {
        setSessionStarted(true);
        await updateSessionProgress(userId, sessionNumber, {
          session_started: true,
          session_start_time: Date.now(),
          video_started: true,
          video_start_time: Date.now(),
          video_progress_percentage: 0,
          video_watch_time_seconds: 0
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error(`Error starting session: ${error.message}`);
      return false;
    }
  };

  const navigateToSession1 = () => {
    navigate('/session01');
  };

  const startRewatch = () => {
    if (playerRef.current) {
      playerRef.current.seekTo(0, 'seconds');
      setPlayedSeconds(0);
      setIsPlaying(true);
    }
  };

  const startIntroduction = async () => {
    if (userId) {
      await handleSessionStart(userId, currentSessionNumber);
    }
    setSessionStarted(true);
  };

  const fetchSessionProgress = async (userId) => {
    if (!userId) return;
    try {
      setLoading(true);
      const sessionStatus = await fetchSessionStatusFast(userId, currentSessionNumber);
      
      setSessionCompleted(sessionStatus.sessionFullyCompleted || sessionStatus.isSessionCompleted || false);
      setIsVideoCompleted(sessionStatus.videoWatched || sessionStatus.videoCompleted || false);
      setQuizCompleted(sessionStatus.quizCompleted || false);
      
      if (sessionStatus.videoWatched || sessionStatus.videoCompleted) {
        setCompletedFirstWatch(true);
      }
      
      const wasActuallyStarted = sessionStatus.sessionStarted === true;
      setSessionStarted(wasActuallyStarted);
      
      if (wasActuallyStarted && userId && currentSessionNumber !== null) {
        await handleSessionStart(userId, currentSessionNumber);
      }
      
      let positionToRestore = null;
      if (sessionStatus.lastVideoPosition && sessionStatus.lastVideoPosition > 10) {
        const lastPosition = sessionStatus.lastVideoPosition;
        const videoLength = 900;
        if (lastPosition < (videoLength - 30)) {
          positionToRestore = lastPosition;
        }
      }
      
      if (positionToRestore) {
        setShouldRestorePosition(positionToRestore);
        setPlayedSeconds(positionToRestore);
      } else {
        setShouldRestorePosition(null);
        setPlayedSeconds(0);
      }
    } catch (error) {
      console.error("Error fetching session progress:", error);
      setSessionStarted(false);
      setIsVideoCompleted(false);
      setQuizCompleted(false);
      setSessionCompleted(false);
      setPlayedSeconds(0);
      setShouldRestorePosition(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId) {
      fetchSessionProgress(userId);
    }
  }, [userId, currentSessionNumber]);

  if (loading || authLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#F8F9FA' }}>
        <MainLayout>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: '40px', height: '40px', border: '3px solid #f1f3f4', borderTop: '3px solid #1B365D', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 20px' }} />
              <p style={{ color: '#374151', fontSize: '16px' }}>Loading Introduction...</p>
            </div>
          </div>
        </MainLayout>
      </div>
    );
  }

  if (!sessionStarted && !sessionCompleted && !loading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#F8F9FA' }}>
        <MainLayout>
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', maxWidth: '1000px', margin: '0 auto', width: '100%' }}>
            <div style={{ padding: isMobile ? '24px 20px 0 20px' : '56px 40px 0 40px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                {isMobile && (
                  <button onClick={() => navigate('/dashboard')} style={{ width: '48px', height: '48px', backgroundColor: '#EFF6FF', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #3B82F6', cursor: 'pointer', fontSize: '20px' }}>←</button>
                )}
                <div style={{ backgroundColor: '#1B365D', color: '#FFFFFF', borderRadius: '12px', padding: isMobile ? '8px 16px' : '12px 20px', fontSize: '14px', fontWeight: '600', textAlign: 'center', height: isMobile ? '32px' : '44px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  Introduction
                </div>
                <div style={{ backgroundColor: '#EFF6FF', borderRadius: '12px', padding: isMobile ? '8px 12px' : '12px 16px', border: '1px solid #3B82F6', height: isMobile ? '32px' : '44px' }}>
                  <span style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: '600', color: '#1B365D' }}>Ready to Start</span>
                </div>
              </div>
              <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: isMobile ? '20px 24px' : '32px 40px', border: '1px solid #E5E7EB', textAlign: 'center', marginBottom: '20px' }}>
                <h1 style={{ fontSize: isMobile ? '28px' : '42px', fontWeight: '600', color: '#1B365D', margin: 0, letterSpacing: '-1px' }}>Welcome to Face Forward</h1>
                <p style={{ fontSize: isMobile ? '16px' : '18px', color: '#6B7280', margin: '12px 0 0 0' }}>Your journey to managing orofacial pain starts here</p>
              </div>
            </div>
            <div style={{ flex: 1, padding: isMobile ? '0 20px 20px 20px' : '0 40px 40px 40px' }}>
              <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', border: '1px solid #E5E7EB', padding: isMobile ? '24px' : '40px' }}>
                <div style={{ marginBottom: '32px' }}>
                  <h2 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: '600', color: '#1B365D', margin: '0 0 16px 0' }}>About This Program</h2>
                  <p style={{ fontSize: '16px', color: '#374151', margin: '0 0 20px 0', lineHeight: '1.6' }}>
                    Face Forward is a comprehensive, evidence-based program designed specifically for individuals living with orofacial pain. Through five structured sessions, you'll learn practical skills and strategies to better manage your symptoms and improve your quality of life.
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                    <div style={{ backgroundColor: '#EFF6FF', borderRadius: '12px', padding: '20px', border: '1px solid #3B82F6' }}>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1B365D', margin: '0 0 8px 0' }}>Evidence-Based Techniques</h4>
                      <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>Learn proven strategies from cognitive behavioral therapy and mindfulness practices.</p>
                    </div>
                    <div style={{ backgroundColor: '#ECFDF5', borderRadius: '12px', padding: '20px', border: '1px solid #10B981' }}>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#065F46', margin: '0 0 8px 0' }}>Self-Paced Learning</h4>
                      <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>Complete sessions at your own pace with guided practice opportunities.</p>
                    </div>
                  </div>
                </div>
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', margin: '0 0 16px 0' }}>What to Expect</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {sessionOverview.map((session, index) => (
                      <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', backgroundColor: '#F8F9FA', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', backgroundColor: '#1B365D', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '600' }}>{index + 1}</div>
                        <div>
                          <h5 style={{ fontSize: '14px', fontWeight: '600', color: '#374151', margin: '0 0 2px 0' }}>{session.title}</h5>
                          <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>{session.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <button onClick={startIntroduction} style={{ backgroundColor: '#1B365D', color: '#FFFFFF', border: 'none', borderRadius: '20px', padding: isMobile ? '12px 24px' : '16px 32px', fontSize: isMobile ? '16px' : '18px', fontWeight: '600', cursor: 'pointer', width: '100%', maxWidth: '400px', margin: '0 auto', display: 'block' }}>
                  Start Introduction
                </button>
              </div>
            </div>
          </div>
        </MainLayout>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F8F9FA' }}>
      <MainLayout>
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
          <div style={{ padding: isMobile ? '24px 20px' : '32px 40px', backgroundColor: '#F8F9FA' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: isMobile ? '16px' : '20px' }}>
                {isMobile ? (
                  <button onClick={() => navigate('/dashboard')} style={{ width: '48px', height: '48px', backgroundColor: '#FFFFFF', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E5E7EB', cursor: 'pointer', fontSize: '20px', color: '#374151' }}>←</button>
                ) : (
                  <button onClick={() => navigate('/dashboard')} style={{ backgroundColor: 'transparent', border: 'none', color: '#374151', cursor: 'pointer', fontSize: '16px', fontWeight: '500' }}>← Back to Dashboard</button>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? '16px' : '24px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
                    <div style={{ backgroundColor: '#1B365D', color: '#FFFFFF', borderRadius: '8px', padding: '4px 12px', fontSize: '12px', fontWeight: '600', display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <svg width="12" height="12" fill="currentColor" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                      </svg>
                      INTRODUCTION
                    </div>
                    <div style={{ backgroundColor: sessionCompleted ? '#ECFDF5' : '#F3F4F6', borderRadius: '8px', padding: '4px 12px', border: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: sessionCompleted ? '#10B981' : '#6B7280' }}>
                        {sessionCompleted ? 'Complete' : 
                         isVideoCompleted && quizCompleted ? 'Quiz Complete' :
                         isVideoCompleted ? 'Video Complete' : 
                         'In Progress'}
                      </span>
                      {sessionCompleted && <CheckMarkSVG size={10} color="#10B981" />}
                    </div>
                  </div>
                  <h1 style={{ fontSize: isMobile ? '24px' : '36px', fontWeight: '600', color: '#1B365D', margin: '0 0 8px 0', letterSpacing: '-1px' }}>Welcome & Program Introduction</h1>
                  <p style={{ fontSize: isMobile ? '14px' : '16px', color: '#6B7280', margin: 0 }}>Get oriented with the program and set your expectations</p>
                </div>
              </div>
            </div>
          </div>
          <div style={{ padding: isMobile ? '0 20px 16px 20px' : '0 40px 24px 40px' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto', width: '100%' }}>
              <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', padding: isMobile ? '16px 20px' : '20px 24px', border: '1px solid #E5E7EB', textAlign: 'center' }}>
                {sessionCompleted ? "Excellent! You've completed the introduction including the quiz." : 
                 !isVideoCompleted ? "Watch the introduction video to get started with the program." : 
                 !quizCompleted ? "Complete the quiz to test your understanding." :
                 "Quiz completed! You're ready to start Session 1."}
              </div>
            </div>
          </div>
          <div style={{ flex: 1, padding: isMobile ? '0 20px 20px 20px' : '0 40px 40px 40px', maxWidth: '1475px', margin: '0 auto', width: '100%' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr', gap: '32px' }}>
              <div>
                <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', border: '1px solid #E5E7EB', overflow: 'hidden', marginBottom: '24px', padding: '24px' }}>
                  <div style={{ position: 'relative', backgroundColor: '#000', borderRadius: '12px', overflow: 'hidden', border: '1px solid #E5E7EB', marginBottom: '20px' }}>
                    <ReactPlayer
                      ref={playerRef}
                      url={currentVideo}
                      playing={isPlaying}
                      controls={false}
                      onProgress={handleProgress}
                      onDuration={(duration) => {
                        setVideoDuration(duration);
                        if (shouldRestorePosition !== null && playerRef.current && duration > 0) {
                          setIsRestoring(true);
                          setTimeout(() => {
                            if (playerRef.current) {
                              playerRef.current.seekTo(shouldRestorePosition, 'seconds');
                              setPlayedSeconds(shouldRestorePosition);
                              setTimeout(() => {
                                setIsRestoring(false);
                                setShouldRestorePosition(null);
                              }, 500);
                            }
                          }, 100);
                        }
                      }}
                      onEnded={handleVideoEnd}
                      volume={volume}
                      width="100%"
                      height="auto"
                      style={{ aspectRatio: '16/9' }}
                      config={{
                        file: {
                          attributes: {
                            crossOrigin: 'anonymous'
                          }
                        }
                      }}
                      onReady={() => {
                        // Completely disable native captions since we're using custom display
                        if (playerRef.current) {
                          const internalPlayer = playerRef.current.getInternalPlayer();
                          if (internalPlayer) {
                            // Disable any text tracks
                            if (internalPlayer.textTracks) {
                              const textTracks = internalPlayer.textTracks;
                              for (let i = 0; i < textTracks.length; i++) {
                                textTracks[i].mode = 'disabled';
                              }
                            }
                            // Also remove the track element if it exists
                            const tracks = internalPlayer.querySelectorAll('track');
                            tracks.forEach(track => track.remove());
                          }
                        }
                      }}
                    />
  <div 
                      ref={overlayRef}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '40px',
                        opacity: 0,
                        transition: 'opacity 0.3s ease',
                        pointerEvents: 'auto',
                        background: 'rgba(0, 0, 0, 0.4)'
                      }}
                      onMouseEnter={() => {
                        if (!isVideoCompleted || playedSeconds < videoDuration - 5) {
                          if (overlayRef.current) {
                            overlayRef.current.style.opacity = '1';
                          }
                          
                          if (overlayTimeout) {
                            clearTimeout(overlayTimeout);
                            setOverlayTimeout(null);
                          }
                          
                          const timeout = setTimeout(() => {
                            if (overlayRef.current) {
                              overlayRef.current.style.opacity = '0';
                            }
                          }, 3000);
                          
                          setOverlayTimeout(timeout);
                        }
                      }}
                      onMouseMove={() => {
                        if (!isVideoCompleted || playedSeconds < videoDuration - 5) {
                          if (overlayRef.current) {
                            overlayRef.current.style.opacity = '1';
                          }
                          
                          if (overlayTimeout) {
                            clearTimeout(overlayTimeout);
                          }
                          
                          const timeout = setTimeout(() => {
                            if (overlayRef.current) {
                              overlayRef.current.style.opacity = '0';
                            }
                          }, 3000);
                          
                          setOverlayTimeout(timeout);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isVideoCompleted || playedSeconds < videoDuration - 5) {
                          if (overlayRef.current) {
                            overlayRef.current.style.opacity = '0';
                          }
                          
                          if (overlayTimeout) {
                            clearTimeout(overlayTimeout);
                            setOverlayTimeout(null);
                          }
                        }
                      }}
                    >
                      {isVideoCompleted && playedSeconds >= videoDuration - 5 ? (
                        <div style={{ backgroundColor: '#FFFFFF', borderRadius: '12px', padding: '24px', textAlign: 'center', opacity: 1 }}>
                          <h4 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 8px 0' }}>Video Complete!</h4>
                          <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 16px 0' }}>You can rewatch this video anytime</p>
                          <button onClick={startRewatch} style={{ backgroundColor: '#1B365D', color: '#FFFFFF', border: 'none', borderRadius: '20px', padding: '12px 20px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                            Rewatch Video
                          </button>
                        </div>
                      ) : (
                        <>
                          <button onClick={skipBackward} style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <RewindButtonSVG size={24} />
                            <div style={{ fontSize: '10px' }}>10s</div>
                          </button>
                          <button onClick={handlePlayPause} style={{ width: '80px', height: '80px', borderRadius: '50%', backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {isPlaying ? <PauseButtonSVG size={32} color="#374151" /> : <PlayButtonSVG size={32} color="#374151" />}
                          </button>
                          {isVideoCompleted && (
                            <button onClick={skipForward} style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                              <ForwardButtonSVG size={24} />
                              <div style={{ fontSize: '10px' }}>10s</div>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    
                    {/* Fullscreen captions - ONLY shows in fullscreen */}
                    {isFullscreen && showCaptions && currentCaption && (
                      <div style={{
                        position: 'absolute',
                        bottom: '80px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        color: '#FFFFFF',
                        padding: '16px 32px',
                        borderRadius: '8px',
                        fontSize: '20px',
                        lineHeight: '1.5',
                        textAlign: 'center',
                        maxWidth: '80%',
                        zIndex: 10,
                        pointerEvents: 'none',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)'
                      }}>
                        {currentCaption}
                      </div>
                    )}
                  </div>
                  
                  {/* Custom Caption Display - Normal view only */}
                  {!isFullscreen && showCaptions && currentCaption && (
                    <div style={{
                      backgroundColor: 'rgba(0, 0, 0, 0.85)',
                      color: '#FFFFFF',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      textAlign: 'center',
                      marginBottom: '16px',
                      minHeight: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: 'system-ui, -apple-system, sans-serif'
                    }}>
                      {currentCaption}
                    </div>
                  )}
                  
                  <div style={{ height: '8px', backgroundColor: '#F3F4F6', borderRadius: '4px', position: 'relative', overflow: 'hidden', marginBottom: '16px', border: '1px solid #E5E7EB', cursor: isVideoCompleted ? 'pointer' : 'default' }} onClick={handleProgressBarClick}>
                    <div style={{ height: '100%', width: `${(playedSeconds / videoDuration) * 100}%`, backgroundColor: '#1B365D', borderRadius: '4px' }} />
                  </div>
                  {!isMobile && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <button onClick={handlePlayPause} style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#1B365D', color: '#FFFFFF', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none' }}>
                          {isPlaying ? <PauseButtonSVG size={16} color="#FFFFFF" /> : <PlayButtonSVG size={16} color="#FFFFFF" />}
                        </button>
                        <button onClick={skipBackward} style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '20px', cursor: 'pointer', padding: '6px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <RewindButtonSVG size={12} />10s
                        </button>
                        {isVideoCompleted && (
                          <button onClick={skipForward} style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '20px', cursor: 'pointer', padding: '6px 12px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            10s<ForwardButtonSVG size={12} />
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{ fontSize: '14px', fontFamily: 'monospace' }}>{formatTime(playedSeconds)} / {formatTime(videoDuration)}</span>
                        <div style={{ position: 'relative' }} className="volume-control">
                          <button
                            onClick={() => setShowVolumeSlider(!showVolumeSlider)}
                            style={{
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E5E7EB',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              padding: '8px',
                              display: 'flex'
                            }}
                          >
                            <svg width="16" height="16" fill="#374151" viewBox="0 0 24 24">
                              {volume === 0 ? <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/> : volume < 0.5 ? <path d="M7 9v6h4l5 5V4l-5 5H7z"/> : <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>}
                            </svg>
                          </button>
                          
                          {showVolumeSlider && (
                            <div 
                              style={{
                                position: 'absolute',
                                bottom: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                marginBottom: '8px',
                                backgroundColor: '#FFFFFF',
                                border: '1px solid #E5E7EB',
                                borderRadius: '8px',
                                padding: '16px 12px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                zIndex: 10,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                width: '48px'
                              }}
                            >
                              <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={volume}
                                onChange={(e) => setVolume(parseFloat(e.target.value))}
                                orient="vertical"
                                style={{
                                  WebkitAppearance: 'slider-vertical',
                                  width: '4px',
                                  height: '100px',
                                  cursor: 'pointer',
                                  background: `linear-gradient(to top, #3B82F6 ${volume * 100}%, #E5E7EB ${volume * 100}%)`,
                                  borderRadius: '2px',
                                  outline: 'none'
                                }}
                              />
                              <div style={{
                                textAlign: 'center',
                                fontSize: '11px',
                                color: '#6B7280',
                                fontWeight: '600',
                                marginTop: '12px'
                              }}>
                                {Math.round(volume * 100)}%
                              </div>
                            </div>
                          )}
                        </div>
                        <button
                          onClick={handleCaptionToggle}
                          style={{
                            backgroundColor: showCaptions ? '#1B365D' : '#FFFFFF',
                            border: '1px solid #E5E7EB',
                            borderRadius: '8px',
                            color: showCaptions ? '#FFFFFF' : '#374151',
                            cursor: 'pointer',
                            padding: '8px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease'
                          }}
                          title={showCaptions ? 'Hide Captions' : 'Show Captions'}
                        >
                          <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M19 4H5c-1.11 0-2 .9-2 2v12c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-8 7H9.5v-.5h-2v3h2V13H11v1c0 .55-.45 1-1 1H7c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1zm7 0h-1.5v-.5h-2v3h2V13H18v1c0 .55-.45 1-1 1h-3c-.55 0-1-.45-1-1v-4c0-.55.45-1 1-1h3c.55 0 1 .45 1 1v1z"/>
                          </svg>
                        </button>
                        <button onClick={handleFullscreen} style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', padding: '8px', display: 'flex' }} title="Fullscreen">
                          <FullscreenSVG size={16} />
                        </button>
                      </div>
                    </div>
                  )}
                  {isVideoCompleted && (
                    <div style={{ marginTop: '16px', backgroundColor: '#ECFDF5', color: '#10B981', padding: '12px 16px', borderRadius: '12px', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #10B981' }}>
                      <CheckMarkSVG size={16} color="#10B981" /> Video Completed
                    </div>
                  )}
                </div>
              </div>
              <div>
                <div style={{ backgroundColor: '#FFFFFF', borderRadius: '16px', border: '1px solid #E5E7EB', padding: '24px' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 20px 0' }}>Knowledge Check</h3>
                  
                  {!isVideoCompleted ? (
                    <div style={{ textAlign: 'center', padding: '32px 0' }}>
                      <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#F8F9FA', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', border: '1px solid #E5E7EB' }}>
                        <LockSVG size={32} color="#374151" />
                      </div>
                      <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>Quiz Locked</h4>
                      <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 24px 0' }}>Complete the video to unlock the quiz</p>
                      <div style={{ height: '8px', backgroundColor: '#F3F4F6', borderRadius: '4px', overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                        <div style={{ height: '100%', backgroundColor: '#1B365D', borderRadius: '4px', width: `${videoDuration > 0 ? (playedSeconds / videoDuration) * 100 : 0}%`, transition: 'width 0.3s ease' }} />
                      </div>
                      <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>
                        {videoDuration > 0 ? Math.floor((playedSeconds / videoDuration) * 100) : 0}% complete
                      </p>
                    </div>
                  ) : quizCompleted && !showQuiz ? (
                    <div>
                      <div style={{ textAlign: 'center', padding: '32px 0 24px' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>✓</div>
                        <h4 style={{ fontSize: '18px', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>Quiz Completed!</h4>
                        <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 24px 0' }}>Great job completing the quiz!</p>
                      </div>
                      
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <button
                          onClick={() => setShowQuiz("review")}
                          style={{
                            backgroundColor: '#F8F9FA',
                            color: '#374151',
                            border: '1px solid #E5E7EB',
                            borderRadius: '24px',
                            padding: '12px 20px',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            fontFamily: 'system-ui, -apple-system, sans-serif',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px'
                          }}
                        >
                          <CheckMarkSVG size={14} color="#374151" /> Review Answers
                        </button>
                      </div>

                      <div style={{ backgroundColor: '#EFF6FF', borderRadius: '12px', padding: '20px', marginTop: '24px', border: '1px solid #3B82F6' }}>
                        <h5 style={{ fontSize: '16px', fontWeight: '600', color: '#1B365D', margin: '0 0 8px 0' }}>Up Next: Session 1</h5>
                        <h6 style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 4px 0' }}>Taking Charge of Orofacial Pain</h6>
                        <p style={{ fontSize: '13px', color: '#6B7280', margin: '0 0 16px 0' }}>Learn about chronic pain and practice deep breathing techniques</p>
                        <button onClick={navigateToSession1} style={{ backgroundColor: '#1B365D', color: '#FFFFFF', border: 'none', borderRadius: '20px', padding: '12px 20px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', width: '100%' }}>
                          Start Session 1 →
                        </button>
                      </div>
                    </div>
                  ) : showQuiz === "review" ? (
                    <div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '20px'
                      }}>
                        <button
                          onClick={() => setShowQuiz(null)}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: '#374151',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            opacity: 0.8
                          }}
                        >
                          ← Back
                        </button>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151'
                        }}>
                          Review Mode
                        </span>
                      </div>
                      <QuizSession00 
                        showAnswers={true} 
                        reviewMode={true}
                        requireCorrectAnswer={false}
                        onComplete={() => setShowQuiz(null)}
                        resetQuiz={false}
                      />
                    </div>
                  ) : showQuiz === "start" ? (
                    <div>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '20px'
                      }}>
                        <button
                          onClick={() => setShowQuiz(null)}
                          style={{
                            backgroundColor: 'transparent',
                            border: 'none',
                            color: '#374151',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500',
                            opacity: 0.8
                          }}
                        >
                          ← Back
                        </button>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: '#374151'
                        }}>
                          Quiz
                        </span>
                      </div>
                      <QuizSession00 
                        onComplete={handleQuizCompletion} 
                        resetQuiz={false}
                        showAnswers={false}
                        reviewMode={false}
                        requireCorrectAnswer={true}
                      />
                    </div>
                  ) : (
                    <div>
                      <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 24px 0' }}>
                        Test your understanding of the introduction content.
                      </p>
                      <button
                        onClick={() => setShowQuiz("start")}
                        style={{
                          backgroundColor: '#EFF6FF',
                          color: '#1B365D',
                          border: '1px solid #3B82F6',
                          borderRadius: '24px',
                          padding: '12px 24px',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          width: '100%'
                        }}
                      >
                        Start Quiz
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </MainLayout>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        input[type="range"][orient="vertical"] { writing-mode: bt-lr; -webkit-appearance: slider-vertical; width: 4px; height: 100px; padding: 0; }
        input[type="range"]::-webkit-slider-thumb { -webkit-appearance: none; background: #FFF; height: 14px; width: 14px; border-radius: 50%; border: 2px solid #3B82F6; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        input[type="range"]::-moz-range-thumb { background: #FFF; height: 14px; width: 14px; border-radius: 50%; border: 2px solid #3B82F6; cursor: pointer; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
        
        /* Hide native video captions completely */
        video::cue {
          visibility: hidden !important;
          display: none !important;
        }
        video::-webkit-media-text-track-display {
          display: none !important;
        }
        video::-webkit-media-text-track-container {
          display: none !important;
        }
      ` }} />
    </div>
  );
};

export default SessionPage00;
